// Schema manager for handling Zod schemas in the Monaco editor
import { SchrittInitialPayload } from '@core/domain';
import { z, toJSONSchema, config } from 'zod';
import { de } from 'zod/locales';
import {
  STEP_VALIDATION_SCHEMAS,
  VorgangProzessSchritt,
} from './stepValidationSchemas';

export interface SchemaDefinition {
  id: string;
  name: string;
  description: string;
  zodSchema: z.ZodSchema;
  jsonSchema: Record<string, unknown>;
  stepType?: VorgangProzessSchritt;
}

const schemaCache = new Map<string, SchemaDefinition>();

/**
 * Setzt die Sprache der Zod-Validierung auf Deutsch.
 */
config(de());

// Step-based validation schemas
const STEP_SCHEMAS: SchemaDefinition[] = Object.entries(
  STEP_VALIDATION_SCHEMAS,
).map(([stepType, schema]) => ({
  id: `step-${stepType.toLowerCase()}`,
  name: `Step: ${stepType}`,
  description: `Validation schema for ${stepType} step payload`,
  zodSchema: schema,
  jsonSchema: toJSONSchema(schema, { unrepresentable: 'any' }),
  stepType: stepType as VorgangProzessSchritt,
}));

export class SchemaManager {
  private static instance: SchemaManager;

  private constructor() {
    // Register step-based schemas
    STEP_SCHEMAS.forEach((schema) => {
      schemaCache.set(schema.id, schema);
    });
  }

  public static getInstance(): SchemaManager {
    if (!SchemaManager.instance) {
      SchemaManager.instance = new SchemaManager();
    }
    return SchemaManager.instance;
  }

  public getSchema(id: string): SchemaDefinition | undefined {
    return schemaCache.get(id);
  }

  /**
   * Get all available schemas
   */
  public getAllSchemas(): SchemaDefinition[] {
    return Array.from(schemaCache.values());
  }

  /**
   * Add or update a schema
   */
  public addSchema(schema: SchemaDefinition): void {
    schemaCache.set(schema.id, schema);
  }

  /**
   * Create schema definition from Zod schema
   */
  public createSchemaFromZod(
    id: string,
    name: string,
    description: string,
    zodSchema: z.ZodSchema,
  ): SchemaDefinition {
    const schemaDefinition: SchemaDefinition = {
      id,
      name,
      description,
      zodSchema,
      jsonSchema: toJSONSchema(zodSchema, { unrepresentable: 'any' }),
    };

    this.addSchema(schemaDefinition);
    return schemaDefinition;
  }

  /**
   * Validate JSON against a Zod schema
   */
  public validateJson(
    json: unknown,
    schemaId: string,
  ): { isValid: boolean; errors: string[]; data?: unknown } {
    const schemaDefinition = this.getSchema(schemaId);
    if (!schemaDefinition) {
      return { isValid: false, errors: [`Schema '${schemaId}' not found`] };
    }

    try {
      const result = schemaDefinition.zodSchema.safeParse(json);

      if (result.success) {
        return { isValid: true, errors: [], data: result.data };
      } else {
        const errors = result.error.issues.map(
          (err) => `${err.path.join('.')}: ${err.message}`,
        );
        return { isValid: false, errors };
      }
    } catch (error) {
      return { isValid: false, errors: [`Validation error: ${error}`] };
    }
  }

  /**
   * Get JSON schema for Monaco editor (backward compatibility)
   */
  public getJsonSchema(schemaId: string): Record<string, unknown> | undefined {
    const schema = this.getSchema(schemaId);
    return schema?.jsonSchema;
  }

  /**
   * Get schema suggestions for autocomplete
   */
  public getSchemaSuggestions(
    schemaId: string,
    propertyPath: string[] = [],
  ): string[] {
    const schema = this.getSchema(schemaId);
    if (!schema) return [];

    // Use the JSON schema for suggestions (Monaco editor compatibility)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let currentSchema: any = schema.jsonSchema;
    for (const prop of propertyPath) {
      if (
        currentSchema.type === 'object' &&
        currentSchema.properties &&
        currentSchema.properties[prop]
      ) {
        currentSchema = currentSchema.properties[prop];
      } else {
        return [];
      }
    }

    // Return available properties
    if (currentSchema.type === 'object' && currentSchema.properties) {
      return Object.keys(currentSchema.properties);
    }

    return [];
  }

  /**
   * Get Zod schema for type-safe operations
   */
  public getZodSchema(schemaId: string): z.ZodSchema | undefined {
    const schema = this.getSchema(schemaId);
    return schema?.zodSchema;
  }

  /**
   * Get schema for a specific step type
   */
  public getSchemaForStep(
    stepType: VorgangProzessSchritt,
  ): SchemaDefinition | undefined {
    return this.getSchema(`step-${stepType.toLowerCase()}`);
  }

  /**
   * Validate JSON against a step payload schema
   */
  public validateStepPayload(
    json: unknown,
    stepType: VorgangProzessSchritt,
  ): {
    isValid: boolean;
    errors: string[];
    data?: SchrittInitialPayload;
    zodIssues?: z.ZodIssue[];
  } {
    const schemaDefinition = this.getSchemaForStep(stepType);
    if (!schemaDefinition) {
      return {
        isValid: false,
        errors: [`Schema for step '${stepType}' not found`],
      };
    }

    try {
      const result = schemaDefinition.zodSchema.safeParse(json);

      if (result.success) {
        return {
          isValid: true,
          errors: [],
          data: result.data as SchrittInitialPayload,
        };
      } else {
        const errors = result.error.issues.map(
          (err) => `${err.path.join('.')}: ${err.message}`,
        );
        return { isValid: false, errors, zodIssues: result.error.issues };
      }
    } catch (error) {
      return { isValid: false, errors: [`Validation error: ${error}`] };
    }
  }

  /**
   * Get all available step types
   */
  public getAvailableStepTypes(): VorgangProzessSchritt[] {
    return Object.values(VorgangProzessSchritt);
  }

  /**
   * Get all step-based schemas
   */
  public getStepSchemas(): SchemaDefinition[] {
    return Array.from(schemaCache.values()).filter(
      (schema) => schema.stepType !== undefined,
    );
  }
}

export const schemaManager = SchemaManager.getInstance();
